import React, { useCallback, useMemo, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import { formatMoney, formatNumber } from '../../lib/formatMoney';
import {
  buildSemanticSeries,
  granularityLabel,
} from '../../lib/timeSeriesAggregation';
import { useTheme } from '../../context/ThemeContext';

const CHART_FONT = 'Inter, ui-sans-serif, system-ui, sans-serif';

function formatTooltipDate(dateStr, granularity) {
  if (granularity === 'month') return dateStr.slice(0, 7);
  return dateStr;
}

export function TimeSeriesChart({
  id,
  rawPoints = [],
  valueKey = 'value',
  valueLabel = 'Value',
  currencyCode = 'DKK',
  className = '',
  height = 260,
  ariaLabel = 'Time series chart',
  /** 'line' (area line) | 'column' (vertical bars) */
  seriesType = 'column',
}) {
  const { chartLine } = useTheme();
  const [zoomRange, setZoomRange] = useState({ start: 0, end: 100 });
  const isColumn = seriesType !== 'line';

  const sortedRaw = useMemo(
    () => [...rawPoints].sort((a, b) => String(a.date).localeCompare(String(b.date))),
    [rawPoints]
  );

  const view = useMemo(
    () => buildSemanticSeries(sortedRaw, zoomRange.start, zoomRange.end, valueKey),
    [sortedRaw, zoomRange.start, zoomRange.end, valueKey]
  );

  const formatValue = useCallback(
    (value) => formatMoney(value, { currency: currencyCode }),
    [currencyCode]
  );

  const option = useMemo(() => {
    const categories = view.points.map((point) => point.label);
    const values = view.points.map((point) => point.value);
    const dense = view.points.length > 40;
    const veryDense = view.points.length > 80;

    const series = isColumn
      ? [
          {
            name: valueLabel,
            type: 'bar',
            data: values,
            barMaxWidth: veryDense ? 8 : dense ? 14 : 28,
            barCategoryGap: veryDense ? '28%' : dense ? '35%' : '42%',
            itemStyle: {
              color: chartLine.line,
              borderRadius: [3, 3, 0, 0],
            },
            emphasis: {
              itemStyle: {
                color: chartLine.tooltipAccent || chartLine.line,
              },
            },
            large: values.length > 500,
          },
        ]
      : [
          {
            name: valueLabel,
            type: 'line',
            smooth: 0.35,
            data: values,
            showSymbol: !dense,
            symbol: 'circle',
            symbolSize: 6,
            lineStyle: { width: 2.5, color: chartLine.line },
            itemStyle: { color: chartLine.line, borderColor: '#fff', borderWidth: 2 },
            areaStyle: {
              color: {
                type: 'linear',
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  { offset: 0, color: chartLine.fillTop },
                  { offset: 1, color: chartLine.fillBottom },
                ],
              },
            },
            sampling: values.length > 200 ? 'lttb' : undefined,
            large: values.length > 500,
          },
        ];

    return {
      animationDuration: 280,
      grid: {
        left: 8,
        right: 12,
        top: 16,
        bottom: 52,
        containLabel: true,
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: isColumn
          ? { type: 'shadow', shadowStyle: { color: 'rgba(15, 23, 42, 0.06)' } }
          : { type: 'line' },
        backgroundColor: '#ffffff',
        borderColor: '#e2e8f0',
        borderWidth: 1,
        textStyle: { color: '#334155', fontSize: 11, fontFamily: CHART_FONT },
        formatter(params) {
          const row = params?.[0];
          if (!row) return '';
          const point = view.points[row.dataIndex];
          if (!point) return '';

          const lines = [
            `<div style="font-weight:600;margin-bottom:4px;color:#201C35">${formatTooltipDate(point.date, view.granularity)}</div>`,
            `<div style="display:flex;justify-content:space-between;gap:12px"><span>${valueLabel}</span><strong style="color:${chartLine.tooltipAccent}">${formatValue(point.value)}</strong></div>`,
          ];

          if (point.invoiceCount != null) {
            lines.push(
              `<div style="display:flex;justify-content:space-between;gap:12px"><span>Invoices</span><strong>${formatNumber(point.invoiceCount)}</strong></div>`
            );
          }

          if (point.averageOrderValue != null) {
            lines.push(
              `<div style="display:flex;justify-content:space-between;gap:12px"><span>AOV</span><strong style="color:${chartLine.tooltipAccent}">${formatValue(point.averageOrderValue)}</strong></div>`
            );
          }

          return lines.join('');
        },
      },
      xAxis: {
        type: 'category',
        data: categories,
        boundaryGap: isColumn,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          color: '#64748b',
          fontSize: 10,
          fontFamily: CHART_FONT,
          interval: dense ? Math.ceil(categories.length / 6) : 'auto',
        },
      },
      yAxis: {
        type: 'value',
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: {
          lineStyle: { type: 'dashed', color: '#cbd5e1' },
        },
        axisLabel: {
          color: '#64748b',
          fontSize: 10,
          fontFamily: CHART_FONT,
          formatter: (value) => formatNumber(value),
        },
      },
      dataZoom: [
        {
          type: 'inside',
          start: zoomRange.start,
          end: zoomRange.end,
          zoomOnMouseWheel: true,
          moveOnMouseMove: true,
          moveOnMouseWheel: true,
        },
        {
          type: 'slider',
          start: zoomRange.start,
          end: zoomRange.end,
          height: 18,
          bottom: 6,
          borderColor: '#e2e8f0',
          fillerColor: chartLine.sliderFill,
          handleStyle: { color: chartLine.handle, borderColor: chartLine.handle },
          textStyle: { color: '#64748b', fontSize: 10, fontFamily: CHART_FONT },
        },
      ],
      series,
    };
  }, [view, valueLabel, formatValue, zoomRange.start, zoomRange.end, chartLine, isColumn]);

  const handleDataZoom = useCallback((params) => {
    const payload = params.batch?.[0] || params;
    const start = payload.start ?? 0;
    const end = payload.end ?? 100;
    setZoomRange({ start, end });
  }, []);

  if (!sortedRaw.length) return null;

  return (
    <div className={`flex w-full min-w-0 flex-col gap-1 ${className}`.trim()}>
      <ReactECharts
        key={`${id}-${seriesType}`}
        id={id}
        option={option}
        style={{ width: '100%', height }}
        opts={{ renderer: 'canvas' }}
        notMerge
        lazyUpdate
        onEvents={{ datazoom: handleDataZoom }}
        aria-label={ariaLabel}
      />
      <p className="text-center text-[10px] text-slate-400">
        {granularityLabel(view.granularity)} view · scroll or drag to zoom
      </p>
    </div>
  );
}
